import React, { useState, useEffect } from 'react';
import { AlbumCreate, AlbumStatus, Album } from '../types';

interface AlbumFormProps {
  onSubmit: (data: AlbumCreate & { files?: FileList }) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
  error: string | null;
  initialData?: Album;
}

export const AlbumForm: React.FC<AlbumFormProps> = ({
  onSubmit,
  onCancel,
  loading,
  error,
  initialData,
}) => {
  const [formData, setFormData] = useState<AlbumCreate>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    status: (initialData?.status as AlbumStatus) || AlbumStatus.DRAFT,
  });
  const [files, setFiles] = useState<FileList | null>(null);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        status: (initialData.status as AlbumStatus) || AlbumStatus.DRAFT,
      });
      setFiles(null);
    }
  }, [initialData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) return;
    await onSubmit({ ...formData, files: files || undefined });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    console.log('Files selected:', selectedFiles);

    if (selectedFiles) {
      const invalidFiles: string[] = [];

      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        console.log(`File ${i + 1}:`, {
          name: file.name,
          type: file.type,
          size: file.size,
          lastModified: file.lastModified,
        });

        // Check if file has proper extension and type
        const hasExtension =
          file.name.includes('.') && file.name.split('.').pop()?.length;
        const hasValidType = file.type && file.type.trim() !== '';

        if (!hasExtension || !hasValidType) {
          invalidFiles.push(file.name);
        }
      }

      if (invalidFiles.length > 0) {
        alert(
          `Les fichiers suivants ne sont pas valides (pas d'extension ou de type MIME):\n${invalidFiles.join(
            '\n'
          )}\n\nVeuillez sélectionner des fichiers d'image ou de vidéo avec les bonnes extensions (.jpg, .png, .mp4, etc.)`
        );
        // Clear the input
        e.target.value = '';
        setFiles(null);
        return;
      }
    }

    setFiles(selectedFiles);
  };

  return (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
      <div className='flex justify-between items-center mb-6'>
        <h2 className='text-lg font-semibold text-gray-900'>
          {initialData ? 'Modifier l\'album' : 'Créer un nouvel album'}
        </h2>
      </div>

      {error && (
        <div className='mb-4 p-3 bg-red-50 border border-red-200 rounded-md'>
          <p className='text-red-600 text-sm'>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-4'>
        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Nom de l&apos;album *
          </label>
          <input
            type='text'
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            placeholder="Nom de l'album"
            required
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
            rows={3}
            placeholder="Description de l'album"
          />
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Photos et vidéos
          </label>
          <input
            type='file'
            multiple
            accept='image/*,video/*'
            onChange={handleFileChange}
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          />
          <p className='text-xs text-gray-500 mt-1'>
            Sélectionnez des fichiers d'images (.jpg, .png, .gif, .webp) ou de
            vidéos (.mp4, .webm, .ogg, .avi, .mov). Max 50MB par fichier. Ne pas
            utiliser de liens Google Drive ou autres services cloud.
          </p>
        </div>

        <div>
          <label className='block text-sm font-medium text-gray-700 mb-1'>
            Statut
          </label>
          <select
            value={formData.status}
            onChange={(e) =>
              setFormData({
                ...formData,
                status: e.target.value as AlbumStatus,
              })
            }
            className='w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'
          >
            <option value={AlbumStatus.DRAFT}>Brouillon</option>
            <option value={AlbumStatus.PUBLISHED}>Publié</option>
            <option value={AlbumStatus.PRIVATE}>Privé</option>
          </select>
        </div>

        <div className='flex gap-3 pt-4'>
          <button
            type='submit'
            disabled={loading || !formData.name}
            className='flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {loading ? 'Création...' : 'Créer l&apos;album'}
          </button>
          <button
            type='button'
            onClick={onCancel}
            className='px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50'
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
